import React, { ReactNode } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import { brandFontScope, brandHeading } from '@/utils/brand-typography'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import SegmentedProgress from '@/modules/base/components/segmented-progress/segmented-progress'
import { SquareIconButton } from '@/modules/base/components/buttons'
import OMOLogo from '@assets/images/logo/svg/logo-brand-dark.svg'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

export const REGISTRATION_TOTAL_STEPS = 3

interface RegistrationStepCardProps {
  step: number
  title: ReactNode
  text?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  // mobile: the actions stay at the bottom of the screen while the content scrolls (Weiter, Fertig);
  // without it they follow the content directly, e.g. the links below the e-mail code
  stickyFooter?: boolean
  onClose?: () => void
  dataQsId: string
  className?: string
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 560,
    padding: 40,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    textAlign: 'left',
    // the registration uses the typefaces of the new brand, like the shop checkout
    ...brandFontScope(theme),
    // below 768 px (window width, like the login) a full screen page instead of a floating modal
    [theme.breakpoints.down(768)]: {
      maxWidth: '100%',
      minHeight: '100%',
      padding: '0 24px 24px',
    },
  },
  cardWithStickyFooter: {
    [theme.breakpoints.down(768)]: {
      paddingBottom: 0,
    },
  },
  // the short question can be skipped with the X - on a full screen page it sits in the header bar,
  // vertically centred next to the logo (bar: 16 + 32 + 16 px)
  closeButton: {
    position: 'absolute',
    top: 24,
    right: 24,
    zIndex: 1,
    [theme.breakpoints.down(768)]: {
      top: 32,
      right: 24,
      transform: 'translateY(-50%)',
    },
  },
  // full screen: the logo sits in a header bar across the full width
  logo: {
    display: 'flex',
    marginBottom: 28,
    width: 'fit-content',
    [theme.breakpoints.down(768)]: {
      width: 'auto',
      margin: '0 -24px 24px',
      padding: '16px 24px',
      borderBottom: `1px solid ${theme.palette.brand.divider}`,
    },
  },
  progress: {
    marginBottom: 28,
  },
  // Sora SemiBold 24 px on every screen size, like the headings of the login screens on a phone
  title: {
    margin: 0,
    ...brandHeading(theme, 'h5', 'semibold'),
    [theme.breakpoints.down(768)]: theme.brandTypography.headings.h5.desktop,
    color: theme.palette.brand.text.heading,
  },
  text: {
    margin: '12px 0 0',
    fontSize: theme.fontSizes.smedium,
    lineHeight: 1.5,
    color: theme.palette.brand.text.secondary,
  },
  // 28 px between the blocks - nothing inside adds its own outer spacing
  content: {
    marginTop: 28,
    '& form': {
      margin: 0,
    },
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    marginTop: 28,
  },
  // above the Safari bar and the home indicator
  stickyFooter: {
    [theme.breakpoints.down(768)]: {
      position: 'sticky',
      bottom: 0,
      zIndex: 1,
      marginTop: 'auto',
      padding: '24px 0 calc(16px + env(safe-area-inset-bottom))',
      backgroundColor: theme.palette.background.paper,
    },
  },
}))

const RegistrationStepCard = ({ step, title, text, children, footer, stickyFooter = false, onClose, dataQsId, className }: RegistrationStepCardProps) => {
  const classes = useStyles()

  return (
    <section className={clsx(classes.card, stickyFooter && classes.cardWithStickyFooter, className)} data-qs-id={dataQsId}>
      {onClose && (
        <SquareIconButton
          variant='icon'
          icon={CloseRoundedIcon}
          customIconSize='14px'
          onClick={onClose}
          ariaLabel={lang.registration.close}
          buttonWrapperClass={classes.closeButton}
          dataQsId={`${dataQsId}-close`}
        />
      )}
      <div className={classes.logo}>
        <SVGIcon svg={OMOLogo} width={144} height={32} />
      </div>
      <SegmentedProgress
        className={classes.progress}
        current={step}
        total={REGISTRATION_TOTAL_STEPS}
        label={lang.registration.progress(step, REGISTRATION_TOTAL_STEPS)}
        dataQsId={`${dataQsId}-progress`}
      />
      <h2 className={classes.title}>{title}</h2>
      {text && <p className={classes.text}>{text}</p>}
      {children && <div className={classes.content}>{children}</div>}
      {footer && <div className={clsx(classes.footer, stickyFooter && classes.stickyFooter)}>{footer}</div>}
    </section>
  )
}

export default RegistrationStepCard
